import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { SUPPORT_EMAIL } from '../../config/supportContacts';

const API_URL = process.env.REACT_APP_API_URL;
const RESEND_COOLDOWN_SECONDS = 30;

// Mirrors the backend's api.utils.request_helpers.mask_email, so the OTP
// screen can show a masked email immediately from localStorage (set at
// login) instead of waiting on a userr-detail call that 401s pre-OTP -
// that endpoint requires an "active" session, but this page runs while the
// session is still "pending_2fa".
const maskEmailForDisplay = (email) => {
  if (!email || !email.includes("@")) return "";
  const [local, domain] = email.split("@");
  if (!local || !domain) return "";
  return `${local[0]}•••••@${domain}`;
};

const OtpValidation = () => {
  const { tprmUser, refreshCaraUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  // A fresh code is sent as soon as this page loads (by the login step), so
  // the resend cooldown starts counting down immediately, not just after the
  // user's first manual resend click.
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState("");
  const [firstName, setFirstName] = useState(localStorage.getItem("first_name") || "");
  const [lastName, setLastName] = useState(localStorage.getItem("last_name") || "");
  const [maskedEmail, setMaskedEmail] = useState(() => maskEmailForDisplay(localStorage.getItem("email")));
  const [role, setRole] = useState(localStorage.getItem("role") || "");   //  role from backend/localStorage
  const [showDeactivatedModal, setShowDeactivatedModal] = useState(false); // Control deactivated modal visibility
  const [countdown, setCountdown] = useState(2); // Countdown for bypass mode
  const [isOtpBypass, setIsOtpBypass] = useState(false); // Track bypass mode

  useEffect(() => {
    const username = localStorage.getItem("username");
    if (!username) {
      setMessage("No username found. Please log in first.");
      window.location.replace("/cara_login");
      return;
    }

    // Check if this is OTP bypass mode
    const otpBypassMode = localStorage.getItem("otp_bypass_mode") === "true";
    setIsOtpBypass(otpBypassMode);

    //  Fetch user details
    const fetchUserInfo = async () => {
      try {
        const response = await axios.get(`${API_URL}api/userr-detail/`, {
          withCredentials: true,
        });
        const { first_name, last_name, role, masked_email } = response.data;
        if (first_name) setFirstName(first_name);
        if (last_name) setLastName(last_name);
        if (role) setRole(role);
        if (masked_email) setMaskedEmail(masked_email);
      } catch (error) {
        console.error("Error fetching user details:", error.response || error);
      }
    };

    fetchUserInfo();

    // Handle OTP bypass mode - auto-verify after 2 seconds
    if (otpBypassMode) {
      console.log(" Login Code Bypass mode detected - auto-verifying OTP...");
      setMessage(` Verifying Login Code automatically... Redirecting in ${countdown} seconds`);
      setOtp("****"); // Show placeholder OTP

      // Countdown timer - only create once
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Auto-verify OTP after 2 seconds
      const verificationTimeout = setTimeout(async () => {
        try {
          //  Use the Django session Login Code validation endpoint with empty OTP
          // Backend should handle bypass mode
          const res = await axios.post(
            `${API_URL}api/validate_otp/`,
            { otp: "", bypass_mode: true }, // Send bypass flag
            { withCredentials: true }
          );

          if (res.status === 200) {
            setMessage(" OTP verified successfully!");
            localStorage.setItem("role", res.data.role);

            // Clear bypass flag
            localStorage.removeItem("otp_bypass_mode");

            //  IMPORTANT: Fetch complete user data after OTP verification
            try {
              if (refreshCaraUser) {
                await refreshCaraUser();
                console.log(" User data refreshed after Login Code verification");
              }
            } catch (error) {
              console.error("Error refreshing user data:", error);
            }

            // Redirect to dashboard after successful verification
            setTimeout(() => {
              if (["TPRM", "TPRMUSER", "TPRM USER"].includes(res.data.role)) {
                window.location.replace("/tprm_admin_dashboard");
              } else if (res.data.role === "CARA") {
                // Check role_name for specific auditor routing
                if ((res.data.role_name || "").toLowerCase().trim() === "internal auditor") {
                  window.location.replace("/internal_auditor_dashboard");
                } else if ((res.data.role_name || "").toLowerCase().trim() === "external auditor") {
                  window.location.replace("/external_auditor_dashboard");
                } else if (res.data.role_name === "manager" || res.data.role_name === "project head" || res.data.role_name === "officer" || res.data.role_name === "user") {
                  window.location.replace("/assessment_dashboard");
                } else {
                  window.location.replace("/assessment_dashboard");
                }
              } else {
                window.location.replace("/assessment_dashboard");
              }
            }, 1000);
          }
        } catch (error) {
          if (error.response?.data?.status === "DEACTIVATED") {
            setShowDeactivatedModal(true);
            return;
          }
          console.error("Auto Login Code verification failed:", error);
          setMessage(" Auto verification failed. Please enter Login Code manually.");
          // Clear bypass flag on failure
          localStorage.removeItem("otp_bypass_mode");
        }
      }, 2000); // 2 second delay

      // Cleanup function to clear timers if component unmounts
      return () => {
        clearInterval(countdownInterval);
        clearTimeout(verificationTimeout);
      };
    }
  }, []); // Remove dependencies to prevent re-running

  // Update countdown message in bypass mode
  useEffect(() => {
    if (isOtpBypass && countdown > 0) {
      setMessage(` Verifying Login Code automatically... Redirecting in ${countdown} seconds`);
    } else if (isOtpBypass && countdown === 0) {
      setMessage(" Redirecting to dashboard...");
    }
  }, [countdown, isOtpBypass]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const cooldownInterval = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(cooldownInterval);
  }, [resendCooldown]);

  //  SECURITY: If the browser restores this page from bfcache (e.g. the
  // user hits Back after verifying successfully), force a fresh load instead
  // of showing the stale, already-consumed code form.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const handleValidateOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      //  Use the Django session OTP validation endpoint
      const res = await axios.post(
        `${API_URL}api/validate_otp/`,
        { otp },
        { withCredentials: true }
      );

      if (res.status === 200) {
        setMessage(" Login Code verified successfully!");
        localStorage.setItem("role", res.data.role);

        setTimeout(() => {
          if (["TPRM", "TPRMUSER", "TPRM USER"].includes(res.data.role)) {
            window.location.replace("/tprm_admin_dashboard");
          } else if (res.data.role === "CARA") {
            // Check role_name for specific auditor routing
            if ((res.data.role_name || "").toLowerCase().trim() === "internal auditor") {
              window.location.replace("/internal_auditor_dashboard");
            } else if ((res.data.role_name || "").toLowerCase().trim() === "external auditor") {
              window.location.replace("/external_auditor_dashboard");
            } else if (res.data.role_name === "manager" || res.data.role_name === "project head" || res.data.role_name === "officer" || res.data.role_name === "user") {
              window.location.replace("/assessment_dashboard");
            } else {
              window.location.replace("/assessment_dashboard");
            }
          } else {
            window.location.replace("/assessment_dashboard");
          }
        }, 1500);
      }
    } catch (err) {
      if (err.response?.data?.status === "DEACTIVATED") {
        setShowDeactivatedModal(true);
        setLoading(false);
        return;
      }

      //  SECURITY: The server is the source of truth for attempt counting
      // and lockout - it enforces the real 5-attempt/15-minute lockout. Only
      // force the user back to login when the server actually reports a
      // lockout (429/LOCKED_OUT); otherwise just show its generic message.
      if (err.response?.status === 429 || err.response?.data?.status === "LOCKED_OUT") {
        const username = localStorage.getItem("username");
        setMessage(err.response?.data?.message || "Too many failed attempts. Please try again later.");

        // Determine login page based on user role
        let userRole = (role || localStorage.getItem("role") || "DEFAULT").toUpperCase();

        // Normalize role values (backend sends "TPRMUser" or "TPRM User")
        if (userRole === "TPRM USER" || userRole === "TPRMUSER") {
          userRole = "TPRMUSER";
        }

        const roleLoginMap = {
          TPRM: '/tprm_login',           // TPRM Admin
          TPRMUSER: '/tprm_user_login',  // TPRM User
          VRM: '/vrm_login',
          CARA: '/cara_login',
          CARAA: '/login',
          AUDIT: '/login',
          DEFAULT: '/login'
        };
        const loginPage = roleLoginMap[userRole] || roleLoginMap.DEFAULT;

        // Clear localStorage and redirect to appropriate login page
        localStorage.removeItem(`wrongOtpAttempts_${username}`);
        localStorage.removeItem("username");
        localStorage.removeItem("role");
        setTimeout(() => {
          window.location.replace(loginPage);
        }, 2000);
      } else {
        setMessage(err.response?.data?.message || "The code is incorrect or has expired.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendLoading || resendCooldown > 0 || isOtpBypass) return;

    setResendLoading(true);
    setMessage("");

    try {
      // Try session-based approach first (no username/user_type needed)
      const res = await axios.post(
        `${API_URL}api/resend_otp/`,
        {}, // Empty body for session-based approach
        { withCredentials: true }
      );

      if (res.status === 200) {
        setMessage(" Login Code has been resent to your email successfully!");
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch (err) {
      if (err.response?.data?.status === "DEACTIVATED") {
        setShowDeactivatedModal(true);
        setResendLoading(false);
        return;
      }
      // If session-based fails, try with username + role
      try {
        const username = localStorage.getItem("username");
        if (!username) {
          setMessage("Session expired. Please login again.");
          window.location.replace("/cara_login");
          return;
        }

        const res = await axios.post(
          `${API_URL}api/resend_otp/`,
          {
            username,
            user_type: role || "CARAA"  // Send user_type based on role
          },
          { withCredentials: true }
        );

        if (res.status === 200) {
          setMessage(" Login Code has been resent to your email successfully!");
          setResendCooldown(RESEND_COOLDOWN_SECONDS);
        }
      } catch (fallbackErr) {
        if (fallbackErr.response?.data?.status === "DEACTIVATED") {
          setShowDeactivatedModal(true);
          setResendLoading(false);
          return;
        }
        setMessage(
          fallbackErr.response?.data?.message || " Failed to resend OTP. Please try again."
        );
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="">
      <main id="otp_validation">

          <div className="d-flex align-items-center justify-content-center p-2" style={{ minHeight: '100vh', backgroundColor: '#e2e8f0' }}>
            <div className="card shadow-lg border-0 w-100" style={{ maxWidth: '1200px', borderRadius: '16px', overflow: 'hidden' }}>
              <div className="row g-0" style={{ minHeight: '85vh' }}>
                {/* Left Section (Marketing) */}
                <div className="col-md-7 d-flex flex-column p-5 position-relative" style={{ backgroundColor: '#f4f9ff' }}>
                  <div style={{ position: 'absolute', top: '-10%', right: '-10%', width: '60%', height: '60%', background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(244,249,255,0) 70%)', borderRadius: '50%', zIndex: 0 }}></div>
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%', background: 'linear-gradient(to top, rgba(13,110,253,0.1), transparent)', zIndex: 0 }}></div>
                  
                  <div style={{ zIndex: 1 }} className="d-flex flex-column h-100 justify-content-center">
                    <div>
                      {/* Logo Area */}
                      <div className="mb-4 d-flex align-items-center">
                        <i className="fas fa-shield-alt text-primary fs-2 me-2"></i>
                        <h2 className="mb-0 fw-bold" style={{ color: '#0f172a' }}>CARA<span style={{ fontWeight: 'normal', color: '#475569' }}>grc</span></h2>
                      </div>

                      <h1 className="fw-bold mb-1" style={{ color: '#1e293b', fontSize: '2.5rem' }}>
                        Secure Access.
                      </h1>
                      <h1 className="fw-bold mb-4" style={{ color: '#1e293b', fontSize: '2.5rem' }}>
                        Two-Factor Authentication.
                      </h1>
                      <div style={{ width: '40px', height: '4px', backgroundColor: '#0d6efd', borderRadius: '2px', marginBottom: '1.5rem' }}></div>
                      
                      <p className="fs-5 mb-5" style={{ color: '#475569', maxWidth: '90%' }}>
                        To protect your account and ensure data privacy, CARAgrc requires a two-step verification process.
                      </p>

                      {/* Info Box */}
                      <div className="p-3 mb-4 rounded-4 d-flex align-items-center shadow-sm" style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', maxWidth: '90%' }}>
                        <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-4" style={{ width: '60px', height: '60px', minWidth: '60px' }}>
                          <i className="fas fa-lock text-white fs-4"></i>
                        </div>
                        <p className="mb-0 fw-medium" style={{ color: '#1e293b', fontSize: '0.95rem' }}>
                          Check your email inbox for the 6-digit verification code. Please do not share this code with anyone.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Section (Form) */}
                <div className="col-md-5 p-4 p-md-5 bg-white d-flex flex-column justify-content-center position-relative">
                  <div className="w-100 mx-auto" style={{ maxWidth: '420px' }}>
                    <div className="text-center mb-4">
                
                <div className="d-flex align-items-center justify-content-center p-2 mb-4 mx-auto text-nowrap" style={{ backgroundColor: '#eff6ff', color: '#3b82f6', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, width: 'fit-content' }}>
                  <i className="fas fa-lock me-2"></i> Identity Verification Required
                </div>
                
                <h2 className="fw-bold mb-2" style={{ color: '#0f172a' }}>
                  Welcome, {firstName} {lastName}!
                </h2>
                <p className="text-muted mb-4 px-2" style={{ fontSize: '1.05rem' }}>
                  We sent a 6-digit verification code to your registered email address{maskedEmail ? ":" : "."}
                  {maskedEmail && (
                    <>
                      <br />
                      <strong style={{ color: '#0f172a' }}>{maskedEmail}</strong>
                    </>
                  )}
                </p>
              </div>

              <form onSubmit={handleValidateOtp} className="w-100 px-md-3">
                <div className="mb-4 text-center">
                  <label className="form-label fw-semibold text-muted small text-start w-100">6-digit Verification Code</label>
                  <input
                    type="text"
                    className="form-control form-control-lg bg-light text-center fw-bold mx-auto"
                    style={{ letterSpacing: '0.5rem', fontSize: '1.5rem', borderRadius: '10px', border: '2px solid #e2e8f0', maxWidth: '300px', ...(isOtpBypass ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}) }}
                    placeholder={isOtpBypass ? "Auto-verifying..." : "••••••"}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    maxLength={6}
                    required
                    disabled={isOtpBypass}
                  />
                </div>

                <div className="d-flex justify-content-center mb-3">
                  <button
                    type="submit"
                    className="btn btn-primary py-2 px-5 fw-bold fs-6"
                    style={{ borderRadius: '10px', backgroundColor: '#0d6efd', minWidth: '200px' }}
                    disabled={loading || isOtpBypass}
                  >
                    {loading ? "Verifying..." : isOtpBypass ? "Auto-verifying..." : "Verify and Continue"}
                  </button>
                </div>

                <div className="text-center mb-3">
                  <button
                    type="button"
                    className="text-decoration-none border-0 bg-transparent p-0 small fw-semibold"
                    onClick={handleResendOtp}
                    disabled={resendLoading || isOtpBypass || resendCooldown > 0}
                    style={{
                      color: '#0d6efd',
                      cursor: (isOtpBypass || resendCooldown > 0) ? 'not-allowed' : 'pointer',
                      opacity: (isOtpBypass || resendCooldown > 0) ? 0.6 : 1
                    }}
                  >
                    {resendLoading
                      ? "Sending..."
                      : isOtpBypass
                        ? "Auto-verification in progress..."
                        : resendCooldown > 0
                          ? `Resend code (${resendCooldown}s)`
                          : "Resend code"}
                  </button>
                </div>

                {message && (
                  <p className={`small mb-3 text-center fw-semibold ${message.includes("successfully") ? "text-success" : "text-danger"}`}>
                    {message}
                  </p>
                )}

                <div className="p-3 bg-light rounded-3 mt-4 text-center" style={{ border: '1px solid #e2e8f0' }}>
                  <p className="text-muted small mb-1">Check your Spam or Junk folder if you don't see the email.</p>
                  <p className="text-muted small mb-0">This code will expire in 5 minutes.</p>
                </div>
              </form>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </main>

      {/* Deactivated Modal */}
      {showDeactivatedModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '15px' }}>
              <div className="modal-header bg-danger text-white" style={{ borderTopLeftRadius: '15px', borderTopRightRadius: '15px' }}>
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-person-x-fill me-2"></i>
                  Account Deactivated
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowDeactivatedModal(false);
                    window.location.replace("/cara_login");
                  }}
                ></button>
              </div>
              <div className="modal-body p-4 text-center">
                <div className="mb-4">
                  <i className="bi bi-exclamation-octagon text-danger" style={{ fontSize: '4rem' }}></i>
                </div>
                <h4 className="fw-bold mb-3">Login Restricted</h4>
                <p className="text-muted mb-4" style={{ fontSize: '1.1rem' }}>
                  Your account has been <strong>deactivated</strong>. You cannot login at this time.
                </p>
                <div className="bg-light p-3 rounded-4 mb-4">
                  <p className="mb-0 text-dark">
                    Please contact our support team at <strong>{SUPPORT_EMAIL}</strong> or your administrator to reactivate your account.
                  </p>
                </div>
              </div>
              <div className="modal-footer bg-light p-3" style={{ borderBottomLeftRadius: '15px', borderBottomRightRadius: '15px' }}>
                <button
                  type="button"
                  className="btn btn-secondary px-4 shadow-sm"
                  onClick={() => {
                    setShowDeactivatedModal(false);
                    window.location.replace("/cara_login");
                  }}
                >
                  Close
                </button>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="btn btn-danger px-4 shadow-sm">
                  Contact Support
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deactivated Modal Backdrop */}
      {showDeactivatedModal && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
};

export default OtpValidation;